import { notification } from 'antd';
import { Dispatch } from '@reduxjs/toolkit';
import ApiService from 'utils/ApiCalls';
import tasksSlice from './tasksSlice';
import complianceEstSlice, { complianceEstOp } from '../ComplienceEst';
import { ITask, ITasksResult, ITasksToShow } from './types';
import { LanguageList } from '../globalTypes';

const {
  set_loading,
  set_tasks,
  set_tasks_to_show,
  add_new_task,
  delete_task,
  edit_task,
  reset_tasks_to_save
} = tasksSlice.actions;

const { set_save_clicked, set_tasks_updated } = complianceEstSlice.actions;

const fetchTasks = async (
  lang: LanguageList,
  filters: Record<string, string[]>
) => {
  try {
    const response = await ApiService.getTasks(lang, filters);
    return response.data;
  } catch (error) {
    return [];
  }
};

const setTasks = (tasks: ITasksResult[]) => (dispatch: Dispatch) => {
  try {
    dispatch(set_loading(true));
    dispatch(set_tasks(tasks));
  } catch (error) {
    console.error(error);
  }
};

const addNewTask = (sol_id: number) => (dispatch: Dispatch) => {
  try {
    dispatch(set_loading(true));
    dispatch(add_new_task(sol_id));
  } catch (error) {
    console.error(error);
  }
};

const setTasksToShow = (tasksToShow: ITasksToShow) => (dispatch: Dispatch) => {
  try {
    dispatch(set_loading(true));
    dispatch(set_tasks_to_show(tasksToShow));
  } catch (error) {
    console.error(error);
  }
};

const editTaskValue =
  (tskId: string | number, taskData: ITask, key: string, value: string) =>
  (dispatch: Dispatch) => {
    try {
      dispatch(set_loading(true));
      dispatch(edit_task({ tskId, taskData, key, value }));
    } catch (error) {
      console.error(error);
    }
  };

const deleteTask =
  (taskId: number | string, solId: number) => async (dispatch: Dispatch) => {
    try {
      const isTemporary = String(taskId).startsWith('new-');

      if (!isTemporary && typeof taskId === 'number') {
        const response = await ApiService.softDeleteRecord('tasks', taskId);

        notification.success({
          message: 'Success',
          description: 'Task successfully deleted',
          duration: 20
        });
      }

      dispatch(delete_task({ taskId, solId }));
    } catch (error: any) {
      notification.error({
        message: 'Delete failed',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to delete task',
        duration: 6
      });
    }
  };

const saveTasks =
  (data: ITask[], lang: LanguageList) => async (dispatch: Dispatch) => {
    try {
      dispatch(set_loading(true));
      const response = await ApiService.saveTasks(data, lang);

      notification.success({
        message: response.status,
        description: response.data?.message || 'Tasks successfully saved',
        duration: 20
      });

      dispatch(set_loading(false));
      dispatch(set_save_clicked(false));
      dispatch(reset_tasks_to_save());
      dispatch(set_tasks_updated(true));
    } catch (error: any) {
      dispatch(set_loading(false));
      notification.error({
        message: 'Save failed',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Something went wrong while saving tasks',
        duration: 6
      });
    }
  };

export default {
  fetchTasks,
  setTasks,
  setTasksToShow,
  addNewTask,
  deleteTask,
  saveTasks,
  editTaskValue
};
